'use client';

import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AnimateOnView } from "@/components/ui/AnimateOnView";
import { ICON_MAP, DEFAULT_CATEGORY_ICON } from "@/constants/categoryIcons";
import { categoriesAPI, type HomeCategory } from "@/lib/categoriesApi";
import { useCategoryFilter } from "@/context/CategoryFilterContext";
import clsx from "clsx";

export default function CategoryButtons() {
  // Temporariamente oculto por solicitação do usuário
  return null;

  const [categories, setCategories] = useState<HomeCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedCategoryId, setSelectedCategory, clearFilter } = useCategoryFilter();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getHomeCategories();
        if (response.success) {
          setCategories(response.categories);
        }
      } catch (error) {
        console.error('Erro ao carregar categorias:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return (
      <section id="categorias" className="relative mt-4 sm:mt-6">
        <div className="relative overflow-hidden rounded-3xl">
          <div className="relative z-10 flex flex-col gap-2" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-white/60">
              Navegue por categorias
            </span>
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="h-11 w-32 animate-pulse rounded-2xl bg-white/10"
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  return (
    <section id="categorias" className="relative mt-4 sm:mt-6">
      <div className="relative overflow-hidden rounded-3xl">
        <div className="relative z-10 flex flex-col gap-2" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
          <AnimateOnView className="animate__faster flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand-secondary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-secondary border border-brand-secondary/20">
                Navegue por categorias
              </span>
            </div>
          </AnimateOnView>

          <AnimateOnView delay={300} className="animate__faster flex flex-wrap gap-3">
            {/* Botão "Todos" para limpar filtro */}
            <button
              type="button"
              onClick={clearFilter}
              className={clsx(
                "group inline-flex items-center gap-2 rounded-2xl border px-5 py-2 text-sm font-semibold transition-all hover:-translate-y-1 active:scale-95",
                selectedCategoryId === null
                  ? "border-brand-secondary bg-brand-secondary/20 text-white shadow-[0_0_15px_rgba(34,197,94,0.3)]"
                  : "border-white/10 bg-brand-surface/50 text-slate-300 hover:border-brand-secondary/50 hover:bg-brand-surface hover:text-white"
              )}
            >
              <span className={clsx(
                "flex h-8 w-8 items-center justify-center rounded-xl transition-colors",
                selectedCategoryId === null
                  ? "bg-brand-secondary text-brand-dark"
                  : "bg-white/5 text-slate-400 group-hover:bg-brand-secondary group-hover:text-brand-dark"
              )}>
                <FontAwesomeIcon icon={DEFAULT_CATEGORY_ICON} className="h-3.5 w-3.5" />
              </span>
              Todos
            </button>

            {/* Botões de categorias */}
            {categories.map((category, index) => {
              const icon = (category.icon && ICON_MAP[category.icon]) || DEFAULT_CATEGORY_ICON;
              const isActive = selectedCategoryId === category.id;
              
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setSelectedCategory(category.id, category.name)}
                  className={clsx(
                    "group inline-flex items-center gap-2 rounded-2xl border px-5 py-2 text-sm font-semibold transition-all hover:-translate-y-1 active:scale-95",
                    isActive
                      ? "border-brand-secondary bg-brand-secondary/20 text-white shadow-[0_0_15px_rgba(34,197,94,0.3)]"
                      : "border-white/10 bg-brand-surface/50 text-slate-300 hover:border-brand-secondary/50 hover:bg-brand-surface hover:text-white"
                  )}
                >
                  <span className={clsx(
                    "flex h-8 w-8 items-center justify-center rounded-xl transition-colors",
                    isActive
                      ? "bg-brand-secondary text-brand-dark"
                      : "bg-white/5 text-slate-400 group-hover:bg-brand-secondary group-hover:text-brand-dark"
                  )}>
                    <FontAwesomeIcon icon={icon} className="h-3.5 w-3.5" />
                  </span>
                  {category.name}
                </button>
              );
            })}
          </AnimateOnView>
        </div>
      </div>
    </section>
  );
}
